/**
 * AI Optimization Helper
 * Contains shared logic for processing form data for AI optimization
 */

/**
 * Iterate through all items in form pages, including nested subPages
 * Handles both regular pages and repeater subPages (e.g., supervision form topics)
 * 
 * @param {Array} pages - Array of form pages
 * @param {Function} itemCallback - Callback function to process each item
 *                                   Receives: (item, page) where page is the parent page/subPage
 */
export function forEachFormItem(pages, itemCallback) {
  if (!pages || !Array.isArray(pages)) return;
  
  const processItem = (item, parentPage) => {
    itemCallback(item, parentPage);
    
    if (item.activators && typeof item.activators === 'object') {
      Object.keys(item.activators).forEach(triggerValue => {
        const nestedItems = item.activators[triggerValue];
        if (Array.isArray(nestedItems)) {
          nestedItems.forEach(nestedItem => {
            if (nestedItem && nestedItem.identifier) {
              processItem(nestedItem, parentPage);
            }
          });
        }
      });
    }
  };

  pages.forEach(page => {
    // Process regular page items
    if (page.items && Array.isArray(page.items)) {
      page.items.forEach(item => {
        processItem(item, page);
      });
    }
    
    // Process repeater subPages (e.g., supervision form topics)
    if (page.subPages && Array.isArray(page.subPages)) {
      page.subPages.forEach(subPage => {
        if (subPage.items && Array.isArray(subPage.items)) {
          subPage.items.forEach(item => {
            processItem(item, subPage);
          });
        }
      });
    }
  });
}

/**
 * Build list of changes from AI optimized content
 * @param {Object} optimizedContent - AI optimized content (field name -> optimized value)
 * @param {Array} formPages - Form pages
 * @returns {Array} - List of changes with metadata for display
 */
export function buildChangesList(optimizedContent, formPages) {
  const changes = [];
  
  forEachFormItem(formPages, (item, page) => {
    const fieldLabel = item.title || item.identifier;
    const hasAIContent = fieldLabel && optimizedContent[fieldLabel];
    const isString = typeof item.value === 'string';
    const hasContent = item.value && isString && item.value.trim().length > 0;
    
    // Only include fields with content that have AI suggestions and are different
    if (hasAIContent && hasContent && item.value !== optimizedContent[fieldLabel]) {
      changes.push({
        fieldKey: `${page.id}-${item.identifier}`,
        fieldLabel: fieldLabel,
        originalValue: item.value,
        optimizedValue: optimizedContent[fieldLabel],
        applied: false,
        rejected: false,
        pageId: page.id,
        itemIdentifier: item.identifier,
        item: item // Keep reference to actual form item for updates
      });
    }
  });
  
  return changes;
}

/**
 * Apply an AI optimization change to the form item
 * @param {Object} change - Change object containing item reference and values
 */
export function applyChange(change) {
  change.item.value = change.optimizedValue;
  
  // Store original value for potential revert
  if (!change.item.originalValue) {
    change.item.originalValue = change.originalValue;
  }
  
  change.applied = true;
  change.rejected = false;
}

/**
 * Revert an applied AI optimization change
 * @param {Object} change - Change object containing item reference and original value
 */
export function revertChange(change) {
  change.item.value = change.originalValue;
  delete change.item.originalValue;
  
  change.applied = false;
  change.rejected = false;
}

/**
 * Mark a change as rejected/skipped
 * @param {Object} change - Change object to reject
 */
export function rejectChange(change) {
  change.rejected = true;
  change.applied = false;
}

/**
 * Format content for display (handles both plain text and HTML)
 * @param {string} content - Content to format
 * @returns {string} - Formatted content with proper line breaks
 */
export function formatContentForDisplay(content) {
  if (!content) return '';
  
  // Check if content contains HTML tags
  const hasHtml = /<[a-z][\s\S]*>/i.test(content);
  
  if (hasHtml) {
    // Return HTML as-is for rich text fields
    return content;
  } else {
    // Convert line breaks to <br> for plain text
    return content.replace(/\n/g, '<br>');
  }
}
